/**
 * AR Aging Report (v4)
 * 
 * Queries:
 *   1. ARINVOICE (always) - invoice headers
 *   2. ARINVOICEITEM (always) - line-item math for balance + retainage held
 *   3. ARINVOICEPAYMENT (historical only) - payment application timeline
 *   4. ARRETAINAGERELEASE (always) - release headers (for RELEASEDATE + STATE filter)
 *   5. ARRETAINAGERELEASEENTRY (always) - release line amounts
 * 
 * Retainage release timing: Intacct shows releases starting the day AFTER the release date.
 * We match this by using strict less-than: RELEASEDATE < asOfDate.
 * 
 * Excluded states: Reversed, Reversal, Voided, Deleted
 * Skip logic: hide when balance=0 AND net retainage (held - released) <= 0
 */

import {
  toIntacctDate, isDateCurrent, parseDate,
  buildAgingBuckets, buildPaymentLookup, aggregateLineBalances,
  buildRetainageReleaseLookup,
} from './reportHelpers';

const EXCLUDED_STATES = ['Reversed', 'Reversal', 'Voided', 'Deleted'];

const arAgingReport = {
  id: 'ar-aging',
  name: 'AR Aging Report',
  description: 'Outstanding AR balances by customer with aging buckets. Line-item precision for balances, retainage held/released as separate columns. Historical dates use ARINVOICEPAYMENT for accurate payment timeline.',
  category: 'Accounts Receivable',

  parameters: [
    {
      id: 'asOfDate', label: 'As-of Date', type: 'date',
      default: () => new Date().toISOString().split('T')[0],
      description: 'Calculate aging as of this date'
    },
    {
      id: 'agingBuckets', label: 'Aging Periods (days)', type: 'text',
      default: '30,60,90',
      description: 'Comma-separated boundaries (e.g., 30,60,90 → Current, 1-30, 31-60, 61-90, 90+)'
    },
    {
      id: 'customerFilter', label: 'Customer ID Filter', type: 'text',
      default: '', description: 'Optional: filter to a specific CUSTOMERID (leave blank for all)'
    },
    {
      id: 'includeFullyPaid', label: 'Include Fully Settled', type: 'checkbox',
      default: false, description: 'Show invoices where balance and retainage are both $0'
    }
  ],

  queries: [
    {
      id: 'invoices', object: 'ARINVOICE',
      fields: [
        'RECORDNO', 'RECORDID', 'DOCNUMBER', 'CUSTOMERID', 'CUSTOMERNAME',
        'WHENCREATED', 'WHENDUE', 'WHENPAID', 'WHENPOSTED',
        'TOTALENTERED', 'TOTALDUE', 'TOTALPAID', 'TOTALRETAINED',
        'STATE', 'DESCRIPTION', 'CURRENCY',
        'TERMNAME', 'DUE_IN_DAYS', 'MEGAENTITYID', 'RECORD_URL'
      ],
      buildFilter: (params) => {
        const d = toIntacctDate(params.asOfDate);
        let f = `WHENCREATED <= '${d}'`;
        if (params.customerFilter?.trim()) f += ` AND CUSTOMERID = '${params.customerFilter.trim()}'`;
        return f;
      }
    },
    {
      id: 'lineItems', object: 'ARINVOICEITEM',
      fields: ['ARINVOICE.RECORDNO', 'AMOUNT', 'TOTALPAID', 'TRX_AMOUNTRETAINED'],
      buildFilter: (params) => {
        const d = toIntacctDate(params.asOfDate);
        let f = `WHENCREATED <= '${d}'`;
        if (params.customerFilter?.trim()) f += ` AND CUSTOMERID = '${params.customerFilter.trim()}'`;
        return f;
      }
    },
    {
      id: 'payments', object: 'ARINVOICEPAYMENT',
      fields: ['RECORDNO', 'RECORDKEY', 'PAYMENTDATE', 'AMOUNT', 'STATE'],
      conditional: (params) => !isDateCurrent(params.asOfDate),
      buildFilter: (params) => `PAYMENTDATE <= '${toIntacctDate(params.asOfDate)}'`
    },
    {
      id: 'retainageHeaders', object: 'ARRETAINAGERELEASE',
      fields: ['RECORDNO', 'RELEASEDATE', 'GLPOSTINGDATE', 'STATE'],
      buildFilter: () => 'RECORDNO > 0'
    },
    {
      id: 'retainageEntries', object: 'ARRETAINAGERELEASEENTRY',
      fields: ['RECORDNO', 'RRKEY', 'RETAINAGEINVOICEKEY', 'TRX_AMOUNTRELEASED'],
      buildFilter: () => 'RECORDNO > 0'
    }
  ],

  additionalQueries: [],

  optionalColumns: [
    { id: 'DESCRIPTION2', label: 'Description 2', queryId: 'invoices', group: 'Invoice Details' },
    { id: 'EXTERNALREFNO', label: 'External Ref No', queryId: 'invoices', group: 'Invoice Details' },
    { id: 'SUPDOCID', label: 'Attachment ID', queryId: 'invoices', group: 'Invoice Details' },
    { id: 'BASECURR', label: 'Base Currency', queryId: 'invoices', group: 'Invoice Details' },
    { id: 'DUNNINGCOUNT', label: 'Dunning Count', queryId: 'invoices', group: 'Invoice Details' },
    { id: 'PROJECTCONTRACTID', label: 'Project Contract ID', queryId: 'invoices', group: 'Invoice Details' },
    { id: 'BILLTOPAYTOCONTACTNAME', label: 'Bill-To Contact', queryId: 'invoices', group: 'Invoice Details' },
    { id: 'WHENDISCOUNT', label: 'Discount Date', queryId: 'invoices', group: 'Invoice Details' },
    { id: 'PROJECTID', label: 'Project ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'PROJECTNAME', label: 'Project Name', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'DEPARTMENTID', label: 'Department ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'DEPARTMENTNAME', label: 'Department Name', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'LOCATIONID', label: 'Location ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'LOCATIONNAME', label: 'Location Name', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'ACCOUNTNO', label: 'Account No', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'ACCOUNTTITLE', label: 'Account Title', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'CLASSID', label: 'Class ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'CLASSNAME', label: 'Class Name', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'COSTTYPEID', label: 'Cost Type ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'ITEMID', label: 'Item ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'TASKID', label: 'Task ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'EMPLOYEEID', label: 'Employee ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'VENDORID', label: 'Vendor ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'ENTRYDESCRIPTION', label: 'Line Description', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'PROJECTCONTRACTID', label: 'Contract ID (Line)', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'PROJECTCONTRACTLINEID', label: 'Contract Line ID', queryId: 'lineItems', group: 'Line Item Details' },
  ],

  process: (datasets, params, enabledOptionalColumns = [], aggregatedJoins = {}) => {
    const invoices = datasets.invoices || [];
    const lineItems = datasets.lineItems || [];
    const paymentRecords = datasets.payments || [];
    const retHeaders = datasets.retainageHeaders || [];
    const retEntries = datasets.retainageEntries || [];
    const asOfDate = new Date(params.asOfDate + 'T23:59:59');

    const isCurrentDate = isDateCurrent(params.asOfDate);
    const hasPaymentData = paymentRecords.length > 0;
    const hasLineData = lineItems.length > 0;

    const lineBalances = aggregateLineBalances(lineItems, 'ARINVOICE.RECORDNO');
    const paymentsByInvoice = hasPaymentData ? buildPaymentLookup(paymentRecords) : {};
    const retainageReleased = buildRetainageReleaseLookup(
      retHeaders, retEntries, 'RETAINAGEINVOICEKEY', asOfDate
    );

    // Build optional line item aggregated values
    const lineItemOptionals = enabledOptionalColumns.filter(c => c.queryId === 'lineItems');
    const lineItemAggregated = {};
    if (lineItemOptionals.length > 0) {
      for (const line of lineItems) {
        const pk = String(line['ARINVOICE.RECORDNO'] || '').trim();
        if (!pk) continue;
        if (!lineItemAggregated[pk]) lineItemAggregated[pk] = {};
        for (const col of lineItemOptionals) {
          const val = String(line[col.id] || '').trim();
          if (!val) continue;
          if (!lineItemAggregated[pk][col.id]) lineItemAggregated[pk][col.id] = new Set();
          lineItemAggregated[pk][col.id].add(val);
        }
      }
    }

    const { bucketColumns, getBucketIndex } = buildAgingBuckets(params.agingBuckets);
    const invoiceOptionals = enabledOptionalColumns.filter(c => c.queryId === 'invoices');

    const reportRows = [];
    let grandTotal = 0, grandRetHeld = 0, grandRetReleased = 0;
    const bucketTotals = new Array(bucketColumns.length).fill(0);
    let skippedPaid = 0, skippedState = 0;

    for (const inv of invoices) {
      const totalEntered = parseFloat(inv.TOTALENTERED) || 0;
      const headerRetained = parseFloat(inv.TOTALRETAINED) || 0;
      const state = String(inv.STATE || '').trim();
      const whenDue = parseDate(inv.WHENDUE);
      const rn = String(inv.RECORDNO || '').trim();

      if (EXCLUDED_STATES.includes(state)) { skippedState++; continue; }

      let balanceDue, retainageHeld;
      if (isCurrentDate && hasLineData) {
        // CURRENT: line-item math is most accurate
        const lt = lineBalances[rn];
        if (lt) { balanceDue = lt.balanceDue; retainageHeld = lt.retainageHeld; }
        else { balanceDue = parseFloat(inv.TOTALDUE) || 0; retainageHeld = headerRetained; }
      } else if (hasPaymentData) {
        // HISTORICAL: payment timeline + WHENPAID fallback for credit-applied invoices
        const lt = lineBalances[rn];
        retainageHeld = lt ? lt.retainageHeld : headerRetained;

        const whenPaid = parseDate(inv.WHENPAID);
        if (whenPaid && whenPaid <= asOfDate) {
          // Invoice was fully settled by as-of date (covers credit applications
          // that don't generate ARINVOICEPAYMENT records)
          balanceDue = 0;
        } else {
          const paidAsOf = paymentsByInvoice[rn] || 0;
          balanceDue = totalEntered - paidAsOf - retainageHeld;
        }
      } else {
        balanceDue = parseFloat(inv.TOTALDUE) || 0;
        retainageHeld = headerRetained;
      }

      balanceDue = Math.round(balanceDue * 100) / 100;
      retainageHeld = Math.round(retainageHeld * 100) / 100;
      const released = Math.round((retainageReleased[rn] || 0) * 100) / 100;
      const netRetainage = Math.round((retainageHeld - released) * 100) / 100;

      // Skip if balance is 0 AND all retainage is released (net retainage <= 0)
      if (balanceDue === 0 && netRetainage <= 0 && !params.includeFullyPaid) {
        skippedPaid++; continue;
      }

      let daysPastDue = 0;
      if (whenDue && whenDue < asOfDate) daysPastDue = Math.floor((asOfDate - whenDue) / (1000 * 60 * 60 * 24));

      const bucketIdx = getBucketIndex(daysPastDue);
      const ba = new Array(bucketColumns.length).fill(0);
      ba[bucketIdx] = balanceDue;
      bucketTotals[bucketIdx] += balanceDue;
      grandTotal += balanceDue;
      grandRetHeld += retainageHeld;
      grandRetReleased += released;

      const row = {
        'Customer ID': inv.CUSTOMERID || '',
        'Customer Name': inv.CUSTOMERNAME || '',
        'Invoice Number': inv.RECORDID || '',
        'Document Number': inv.DOCNUMBER || '',
        'Description': inv.DESCRIPTION || '',
        'Invoice Date': inv.WHENCREATED || '',
        'Due Date': inv.WHENDUE || '',
        'GL Post Date': inv.WHENPOSTED || '',
        'Terms': inv.TERMNAME || '',
        'Original Amount': totalEntered,
        'Amount Received': Math.round((totalEntered - balanceDue - retainageHeld) * 100) / 100,
        'Balance Due': balanceDue,
        'Retainage Held': retainageHeld,
        'Retainage Released': released,
        'Days Past Due': daysPastDue,
        'Status': state,
        '_recordUrl': inv.RECORD_URL || '',
        '_entity': inv.MEGAENTITYID || '',
      };

      for (const col of invoiceOptionals) row[col.outputLabel || col.label] = inv[col.id] || '';
      if (lineItemOptionals.length > 0) {
        const agg = lineItemAggregated[rn] || {};
        for (const col of lineItemOptionals) {
          const vals = agg[col.id];
          row[col.outputLabel || col.label] = vals ? [...vals].join(', ') : '';
        }
      }
      bucketColumns.forEach((col, idx) => { row[col] = ba[idx]; });
      reportRows.push(row);
    }

    reportRows.sort((a, b) => {
      const c = (a['Customer ID'] || '').localeCompare(b['Customer ID'] || '');
      return c !== 0 ? c : (b['Days Past Due'] || 0) - (a['Days Past Due'] || 0);
    });

    const columns = [
      'Customer ID', 'Customer Name', 'Invoice Number', 'Document Number', 'Description',
      'Invoice Date', 'Due Date', 'GL Post Date', 'Terms',
      'Original Amount', 'Amount Received', 'Balance Due',
      'Retainage Held', 'Retainage Released',
      'Days Past Due', 'Status',
      ...invoiceOptionals.map(c => c.outputLabel || c.label),
      ...lineItemOptionals.map(c => c.outputLabel || c.label),
      ...bucketColumns
    ];

    const custCount = new Set(reportRows.map(r => r['Customer ID']).filter(Boolean)).size;
    const summaryLines = [
      `As-of Date: ${params.asOfDate}`,
      isCurrentDate ? '(Current — line-item precision)' : hasPaymentData ? '(Historical — ARINVOICEPAYMENT timeline)' : '(Historical — header estimate)',
      `Outstanding: ${reportRows.filter(r => r['Balance Due'] !== 0).length} invoices across ${custCount} customer(s)`,
      `Balance Due: $${grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      `Retainage Held: $${grandRetHeld.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      `Retainage Released: $${grandRetReleased.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      '', ...bucketColumns.map((col, idx) => `${col}: $${bucketTotals[idx].toLocaleString('en-US', { minimumFractionDigits: 2 })}`),
    ];
    if (skippedPaid > 0) summaryLines.push('', `(${skippedPaid} fully-settled invoice(s) excluded)`);
    if (skippedState > 0) summaryLines.push(`(${skippedState} reversed/voided invoice(s) excluded)`);

    return { columns, data: reportRows, summary: { lines: summaryLines, totals: { grandTotal, grandRetHeld, grandRetReleased, bucketTotals, bucketColumns } } };
  },

  formatRules: {
    'Original Amount': { type: 'currency', decimals: 2 },
    'Amount Received': { type: 'currency', decimals: 2 },
    'Balance Due': { type: 'currency', decimals: 2 },
    'Retainage Held': { type: 'currency', decimals: 2 },
    'Retainage Released': { type: 'currency', decimals: 2 },
    'Days Past Due': { type: 'number', decimals: 0 },
    'Invoice Number': { type: 'hyperlink', urlKey: '_recordUrl', entityKey: '_entity' },
    'Current': { type: 'currency', decimals: 2 },
    '1-30 Days': { type: 'currency', decimals: 2 },
    '31-60 Days': { type: 'currency', decimals: 2 },
    '61-90 Days': { type: 'currency', decimals: 2 },
    '90+ Days': { type: 'currency', decimals: 2 },
  }
};

export default arAgingReport;